/**
 * InformationsschreibenPage.tsx — Professionelle Informationsschreiben
 * 
 * DESIGN: Mikro-minimalistisch, professionell, Koop-Farbschema
 * 
 * Features:
 * - Vorlagen für verschiedene Empfänger
 * - Echte PDF-Generierung mit jsPDF (Briefkopf + Fußzeile)
 * - Direktversand per Gmail
 * - CRM-Kontakt-Auswahl als Empfänger
 * - Live-Vorschau des Schreibens
 */

import { useState, useMemo, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useMobileBackHandler } from '@/hooks/useMobileBackHandler';
import jsPDF from 'jspdf';
import {
  FileSignature, Plus, Search, Send, Eye, Edit, Download, Printer,
  FileText, Users, Scale, Wrench, Shield, ChevronRight,
  X, Copy, Mail, User, Loader2,
} from 'lucide-react';
import { useCRMKontakte, type CRMKontakt } from '@/hooks/useCRM';
import { useGmail } from '@/hooks/useGmail';
import { localIsoDate } from '@/lib/date-utils';

// ============================================================
// TYPES
// ============================================================

type EmpfaengerTyp = 'auftraggeber' | 'rechtsanwalt' | 'werkstatt' | 'versicherung';

interface SchreibenVorlage {
  id: string;
  name: string;
  empfaengerTyp: EmpfaengerTyp;
  betreff: string;
  inhalt: string;
  variablen: string[];
  beschreibung: string;
}

// ============================================================
// BRIEFKOPF CONFIG
// ============================================================

const BRIEFKOPF = {
  firma: 'Kfz Sachverständigenbüro Koop',
  inhaber: 'Andreas Koop',
  qualifikation: 'Geprüfter Kfz-Sachverständiger',
  strasse: 'Glatzer Str. 8',
  plz: '58840',
  ort: 'Plettenberg',
  telefon: '02391 / 917 97 70',
  mobil: '0151 / 123 456 78',
  email: 'info@gutachter-koop.de',
  web: 'www.gutachter-koop.de',
  steuernr: 'DE123456789',
  iban: 'DE89 3704 0044 0532 0130 00',
  bic: 'COBADEFFXXX',
  bank: 'Commerzbank',
};

// ============================================================
// VORLAGEN
// ============================================================

const EMPFAENGER_CONFIG: Record<EmpfaengerTyp, { label: string; icon: typeof Users; color: string }> = {
  auftraggeber: { label: 'Auftraggeber', icon: Users, color: '#2563EB' },
  rechtsanwalt: { label: 'Rechtsanwalt', icon: Scale, color: '#7C3AED' },
  werkstatt: { label: 'Werkstatt', icon: Wrench, color: '#D97706' },
  versicherung: { label: 'Versicherung', icon: Shield, color: '#DC2626' },
};

const VORLAGEN: SchreibenVorlage[] = [
  {
    id: 'v1',
    name: 'Auftragsbestätigung Gutachten',
    empfaengerTyp: 'auftraggeber',
    betreff: 'Auftragsbestätigung — Gutachten {{aktenzeichen}}',
    beschreibung: 'Bestätigung des Gutachtenauftrags mit Terminhinweis',
    inhalt: `Sehr geehrte/r {{anrede}} {{name}},

hiermit bestätigen wir den Eingang Ihres Gutachtenauftrags und bedanken uns für Ihr Vertrauen.

Ihr Auftrag wurde unter dem Aktenzeichen {{aktenzeichen}} erfasst.

Fahrzeug: {{fahrzeug}}
Kennzeichen: {{kennzeichen}}

Der Besichtigungstermin ist für den {{termin_datum}} um {{termin_uhrzeit}} Uhr vorgesehen. Bitte bringen Sie folgende Unterlagen mit:

- Fahrzeugschein (Zulassungsbescheinigung Teil I)
- Unfallbericht / Europäischer Unfallbericht
- Versicherungsdaten des Unfallgegners

Sollten Sie Fragen haben, erreichen Sie uns jederzeit unter ${BRIEFKOPF.telefon}.

Mit freundlichen Grüßen

${BRIEFKOPF.inhaber}
${BRIEFKOPF.qualifikation}`,
    variablen: ['anrede', 'name', 'aktenzeichen', 'fahrzeug', 'kennzeichen', 'termin_datum', 'termin_uhrzeit'],
  },
  {
    id: 'v2',
    name: 'Gutachten-Übersendung an Anwalt',
    empfaengerTyp: 'rechtsanwalt',
    betreff: 'Gutachten {{aktenzeichen}} — {{fahrzeug}}',
    beschreibung: 'Übersendung des fertiggestellten Gutachtens an den Rechtsanwalt',
    inhalt: `Sehr geehrte/r {{anrede}} {{name}},

anbei übersenden wir Ihnen das Gutachten zum Schadensfall Ihres Mandanten.

Aktenzeichen: {{aktenzeichen}}
Fahrzeug: {{fahrzeug}}
Kennzeichen: {{kennzeichen}}
Schadenhöhe: {{schadenhoehe}}

Ergebnis der Begutachtung:
- Wiederbeschaffungswert: {{wbw}}
- Reparaturkosten netto: {{reparaturkosten_netto}}
- Reparaturkosten brutto: {{reparaturkosten_brutto}}
- Wertminderung: {{wertminderung}}
- Nutzungsausfall: {{nutzungsausfall}} ({{nutzungsausfall_tage}} Tage)

Das Gutachten wurde nach bestem Wissen und Gewissen erstellt. Unsere Honorarrechnung ist dem Gutachten beigefügt.

Für Rückfragen stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen

${BRIEFKOPF.inhaber}
${BRIEFKOPF.qualifikation}`,
    variablen: ['anrede', 'name', 'aktenzeichen', 'fahrzeug', 'kennzeichen', 'schadenhoehe', 'wbw', 'reparaturkosten_netto', 'reparaturkosten_brutto', 'wertminderung', 'nutzungsausfall', 'nutzungsausfall_tage'],
  },
  {
    id: 'v3',
    name: 'Reparaturfreigabe an Werkstatt',
    empfaengerTyp: 'werkstatt',
    betreff: 'Reparaturfreigabe — {{fahrzeug}} ({{kennzeichen}})',
    beschreibung: 'Information an die Werkstatt über Reparaturfreigabe',
    inhalt: `Sehr geehrte Damen und Herren,

bezüglich des nachfolgend genannten Fahrzeugs teilen wir Ihnen mit, dass die Reparatur gemäß unserem Gutachten freigegeben ist.

Aktenzeichen: {{aktenzeichen}}
Fahrzeug: {{fahrzeug}}
Kennzeichen: {{kennzeichen}}
Halter: {{halter_name}}

Kalkulierte Reparaturkosten:
- Netto: {{reparaturkosten_netto}}
- Brutto: {{reparaturkosten_brutto}}

Bitte beachten Sie, dass die Reparatur gemäß der im Gutachten beschriebenen Instandsetzungsmethode durchzuführen ist. Bei Abweichungen bitten wir um vorherige Rücksprache.

Die Rechnungsstellung erfolgt an die gegnerische Versicherung:
{{versicherung_name}}
Schadennummer: {{schadennummer}}

Mit freundlichen Grüßen

${BRIEFKOPF.inhaber}
${BRIEFKOPF.qualifikation}`,
    variablen: ['aktenzeichen', 'fahrzeug', 'kennzeichen', 'halter_name', 'reparaturkosten_netto', 'reparaturkosten_brutto', 'versicherung_name', 'schadennummer'],
  },
  {
    id: 'v4',
    name: 'Zahlungsaufforderung an Versicherung',
    empfaengerTyp: 'versicherung',
    betreff: 'Zahlungsaufforderung — Schadennr. {{schadennummer}}',
    beschreibung: 'Zahlungsaufforderung für Sachverständigenhonorar',
    inhalt: `Sehr geehrte Damen und Herren,

in vorbezeichneter Schadenangelegenheit erlauben wir uns, Sie an die ausstehende Zahlung unseres Sachverständigenhonorars zu erinnern.

Schadennummer: {{schadennummer}}
Aktenzeichen: {{aktenzeichen}}
Geschädigter: {{geschaedigter_name}}
Fahrzeug: {{fahrzeug}}
Kennzeichen: {{kennzeichen}}

Rechnungsnummer: {{rechnungsnr}}
Rechnungsdatum: {{rechnungsdatum}}
Rechnungsbetrag: {{rechnungsbetrag}}
Fällig seit: {{faellig_seit}}

Wir bitten um umgehende Begleichung des offenen Betrags auf unser nachstehendes Konto:

Kontoinhaber: ${BRIEFKOPF.firma}
IBAN: ${BRIEFKOPF.iban}
BIC: ${BRIEFKOPF.bic}

Sollte die Zahlung bereits veranlasst sein, betrachten Sie dieses Schreiben als gegenstandslos.

Mit freundlichen Grüßen

${BRIEFKOPF.inhaber}
${BRIEFKOPF.qualifikation}`,
    variablen: ['schadennummer', 'aktenzeichen', 'geschaedigter_name', 'fahrzeug', 'kennzeichen', 'rechnungsnr', 'rechnungsdatum', 'rechnungsbetrag', 'faellig_seit'],
  },
  {
    id: 'v5',
    name: 'Stellungnahme an Versicherung',
    empfaengerTyp: 'versicherung',
    betreff: 'Stellungnahme zu Kürzungen — Schadennr. {{schadennummer}}',
    beschreibung: 'Stellungnahme bei Kürzungen des Sachverständigenhonorars',
    inhalt: `Sehr geehrte Damen und Herren,

in vorbezeichneter Schadenangelegenheit nehmen wir Bezug auf Ihr Schreiben vom {{datum_kuerzung}} und die darin vorgenommenen Kürzungen unseres Sachverständigenhonorars.

Schadennummer: {{schadennummer}}
Aktenzeichen: {{aktenzeichen}}
Geschädigter: {{geschaedigter_name}}

Wir weisen die vorgenommenen Kürzungen in vollem Umfang zurück.

Unser Honorar basiert auf einer direkten, unterzeichneten Auftrags- und Honorarvereinbarung mit dem Geschädigten und wurde auf betriebswirtschaftlicher Grundlage kalkuliert.

Gemäß der ständigen Rechtsprechung des Bundesgerichtshofs (BGH) hat der Schädiger bzw. dessen Haftpflichtversicherer das sogenannte Sachverständigenrisiko zu tragen.

Wir fordern Sie daher auf, den gekürzten Betrag in Höhe von {{gekuerzter_betrag}} bis zum {{frist_datum}} auf unser Konto zu überweisen.

Mit freundlichen Grüßen

${BRIEFKOPF.inhaber}
${BRIEFKOPF.qualifikation}`,
    variablen: ['schadennummer', 'aktenzeichen', 'geschaedigter_name', 'datum_kuerzung', 'gekuerzter_betrag', 'frist_datum'],
  },
  {
    id: 'v6',
    name: 'Informationsschreiben an Auftraggeber',
    empfaengerTyp: 'auftraggeber',
    betreff: 'Information zum Stand Ihres Gutachtens — {{aktenzeichen}}',
    beschreibung: 'Allgemeines Informationsschreiben zum Bearbeitungsstand',
    inhalt: `Sehr geehrte/r {{anrede}} {{name}},

wir möchten Sie über den aktuellen Stand Ihres Gutachtenauftrags informieren.

Aktenzeichen: {{aktenzeichen}}
Fahrzeug: {{fahrzeug}}
Kennzeichen: {{kennzeichen}}

Aktueller Status: {{status}}

{{freitext}}

Sollten Sie weitere Fragen haben, stehen wir Ihnen jederzeit gerne zur Verfügung.

Mit freundlichen Grüßen

${BRIEFKOPF.inhaber}
${BRIEFKOPF.qualifikation}`,
    variablen: ['anrede', 'name', 'aktenzeichen', 'fahrzeug', 'kennzeichen', 'status', 'freitext'],
  },
];

// ============================================================
// PDF GENERATION
// ============================================================

function generatePDF(
  empfaenger: { name: string; firma?: string; strasse?: string; plzOrt?: string },
  betreff: string,
  inhalt: string,
  aktenzeichen?: string,
): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = 210;
  const marginL = 25;
  const marginR = 25;
  const textW = pageW - marginL - marginR;
  let y = 20;

  // Briefkopf
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(30, 30, 30);
  doc.text(BRIEFKOPF.firma, marginL, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`${BRIEFKOPF.inhaber} — ${BRIEFKOPF.qualifikation}`, marginL, y);

  // Rechte Seite: Adresse
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  const rightX = pageW - marginR;
  doc.text(BRIEFKOPF.strasse, rightX, 20, { align: 'right' });
  doc.text(`${BRIEFKOPF.plz} ${BRIEFKOPF.ort}`, rightX, 24, { align: 'right' });
  doc.text(`Tel: ${BRIEFKOPF.telefon}`, rightX, 28, { align: 'right' });
  doc.text(BRIEFKOPF.email, rightX, 32, { align: 'right' });

  // Trennlinie
  y += 5;
  doc.setDrawColor(196, 30, 58); // #C41E3A
  doc.setLineWidth(0.5);
  doc.line(marginL, y, pageW - marginR, y);

  // Absender-Zeile
  y += 5;
  doc.setFontSize(6.5);
  doc.setTextColor(160, 160, 160);
  doc.text(`${BRIEFKOPF.firma} · ${BRIEFKOPF.strasse} · ${BRIEFKOPF.plz} ${BRIEFKOPF.ort}`, marginL, y);

  // Empfänger
  y += 8;
  doc.setFontSize(10);
  doc.setTextColor(30, 30, 30);
  doc.setFont('helvetica', 'normal');
  if (empfaenger.firma) {
    doc.text(empfaenger.firma, marginL, y);
    y += 5;
  }
  doc.text(empfaenger.name, marginL, y);
  y += 5;
  if (empfaenger.strasse) {
    doc.text(empfaenger.strasse, marginL, y);
    y += 5;
  }
  if (empfaenger.plzOrt) {
    doc.text(empfaenger.plzOrt, marginL, y);
    y += 5;
  }

  // Datum + Aktenzeichen
  y += 8;
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  const datum = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  doc.text(`Plettenberg, den ${datum}`, rightX, y, { align: 'right' });
  if (aktenzeichen) {
    y += 5;
    doc.text(`Unser Zeichen: ${aktenzeichen}`, rightX, y, { align: 'right' });
  }

  // Betreff
  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(30, 30, 30);
  const betreffLines = doc.splitTextToSize(betreff, textW);
  doc.text(betreffLines, marginL, y);
  y += betreffLines.length * 5 + 5;

  // Inhalt
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(50, 50, 50);
  const lines = doc.splitTextToSize(inhalt, textW);
  
  for (let i = 0; i < lines.length; i++) {
    if (y > 265) {
      // Fußzeile auf aktueller Seite
      addFooter(doc, pageW, marginL, marginR);
      doc.addPage();
      y = 25;
    }
    doc.text(lines[i], marginL, y);
    y += 4.5;
  }

  // Fußzeile
  addFooter(doc, pageW, marginL, marginR);

  return doc;
}

function addFooter(doc: jsPDF, pageW: number, marginL: number, marginR: number) {
  const footerY = 282;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(marginL, footerY - 3, pageW - marginR, footerY - 3);
  
  doc.setFontSize(6.5);
  doc.setTextColor(150, 150, 150);
  doc.setFont('helvetica', 'normal');
  doc.text(`${BRIEFKOPF.firma} · ${BRIEFKOPF.inhaber}`, marginL, footerY);
  doc.text(`Tel: ${BRIEFKOPF.telefon} · ${BRIEFKOPF.email}`, pageW / 2, footerY, { align: 'center' });
  doc.text(`IBAN: ${BRIEFKOPF.iban}`, pageW - marginR, footerY, { align: 'right' });
  doc.text(`USt-IdNr: ${BRIEFKOPF.steuernr} · ${BRIEFKOPF.bank} · BIC: ${BRIEFKOPF.bic}`, pageW / 2, footerY + 3.5, { align: 'center' });
}

// ============================================================
// COMPONENTS
// ============================================================

function BriefkopfPreview({ 
  empfaenger, betreff, inhalt, aktenzeichen 
}: { 
  empfaenger?: { name: string; firma?: string; strasse?: string; plzOrt?: string };
  betreff?: string;
  inhalt?: string;
  aktenzeichen?: string;
}) {
  return (
    <div className="bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden" style={{ fontFamily: 'Georgia, serif' }}>
      {/* Briefkopf */}
      <div className="border-b-2 border-[#C41E3A] px-8 py-5">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>{BRIEFKOPF.firma}</h2>
            <p className="text-sm text-gray-600">{BRIEFKOPF.inhaber} — {BRIEFKOPF.qualifikation}</p>
          </div>
          <div className="text-right text-xs text-gray-500 leading-relaxed">
            <p>{BRIEFKOPF.strasse}</p>
            <p>{BRIEFKOPF.plz} {BRIEFKOPF.ort}</p>
            <p>Tel: {BRIEFKOPF.telefon}</p>
            <p>{BRIEFKOPF.email}</p>
          </div>
        </div>
      </div>

      {/* Absender-Zeile */}
      <div className="px-8 pt-4">
        <p className="text-[9px] text-gray-400 border-b border-gray-200 pb-1 mb-4">
          {BRIEFKOPF.firma} · {BRIEFKOPF.strasse} · {BRIEFKOPF.plz} {BRIEFKOPF.ort}
        </p>
      </div>

      {/* Empfänger */}
      <div className="px-8 mb-4">
        {empfaenger?.firma && <p className="text-sm text-gray-800">{empfaenger.firma}</p>}
        <p className="text-sm text-gray-800">{empfaenger?.name || <span className="text-gray-400 italic">{'{{Empfänger Name}}'}</span>}</p>
        <p className="text-sm text-gray-800">{empfaenger?.strasse || <span className="text-gray-400 italic">{'{{Straße}}'}</span>}</p>
        <p className="text-sm text-gray-800">{empfaenger?.plzOrt || <span className="text-gray-400 italic">{'{{PLZ Ort}}'}</span>}</p>
      </div>

      {/* Datum + AZ */}
      <div className="px-8 mb-4 flex justify-between items-start">
        {aktenzeichen && <p className="text-xs text-gray-500">Unser Zeichen: {aktenzeichen}</p>}
        <p className="text-sm text-gray-600 ml-auto">Plettenberg, den {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
      </div>

      {/* Betreff */}
      {betreff && (
        <div className="px-8 mb-4">
          <p className="text-sm font-bold text-gray-900">{betreff}</p>
        </div>
      )}

      {/* Inhalt */}
      {inhalt && (
        <div className="px-8 pb-6">
          <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line max-h-[400px] overflow-y-auto">
            {inhalt}
          </div>
        </div>
      )}

      {/* Fußzeile */}
      <div className="border-t border-gray-200 px-8 py-3 bg-gray-50">
        <div className="flex justify-between text-[9px] text-gray-400">
          <span>{BRIEFKOPF.firma} · {BRIEFKOPF.inhaber}</span>
          <span>IBAN: {BRIEFKOPF.iban}</span>
        </div>
        <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
          <span>Tel: {BRIEFKOPF.telefon} · {BRIEFKOPF.email}</span>
          <span>USt-IdNr: {BRIEFKOPF.steuernr} · {BRIEFKOPF.bank}</span>
        </div>
      </div>
    </div>
  );
}

function KontaktPicker({ onSelect, onClose }: { onSelect: (k: CRMKontakt) => void; onClose: () => void }) {
  const { kontakte, loading } = useCRMKontakte();
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (!search) return kontakte;
    const s = search.toLowerCase();
    return kontakte.filter(k => 
      k.name?.toLowerCase().includes(s) || 
      k.firma?.toLowerCase().includes(s) || 
      k.email?.toLowerCase().includes(s)
    );
  }, [kontakte, search]);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-end sm:items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:w-[500px] max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-sm font-bold text-gray-900">Kontakt aus CRM wählen</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Name, Firma oder E-Mail suchen..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20"
              autoFocus
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Keine Kontakte gefunden</p>
          ) : (
            filtered.map(k => (
              <button
                key={k.id}
                onClick={() => onSelect(k)}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left"
              >
                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{k.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {[k.firma, k.email, k.kategorie].filter(Boolean).join(' · ')}
                  </p>
                </div>
                {k.email && <Mail className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN PAGE
// ============================================================

export default function InformationsschreibenPage() {
  const [selectedVorlage, setSelectedVorlage] = useState<SchreibenVorlage | null>(null);
  const [activeView, setActiveView] = useState<'vorlagen' | 'erstellt' | 'neu'>('vorlagen');

  // Mobile: Hardware-Back schließt Vorlagen-Detail statt Seite zu verlassen
  useMobileBackHandler(!!selectedVorlage, () => setSelectedVorlage(null));
  const [filterTyp, setFilterTyp] = useState<EmpfaengerTyp | 'alle'>('alle');
  const [search, setSearch] = useState('');
  const [showKontaktPicker, setShowKontaktPicker] = useState(false);
  const [sending, setSending] = useState(false);

  // Form state
  const [empName, setEmpName] = useState('');
  const [empFirma, setEmpFirma] = useState('');
  const [empStrasse, setEmpStrasse] = useState('');
  const [empPlzOrt, setEmpPlzOrt] = useState('');
  const [empEmail, setEmpEmail] = useState('');
  const [betreff, setBetreff] = useState('');
  const [inhalt, setInhalt] = useState('');
  const [aktenzeichen, setAktenzeichen] = useState('');

  const { sendEmail } = useGmail();

  const filteredVorlagen = useMemo(() => {
    return VORLAGEN.filter(v => {
      if (filterTyp !== 'alle' && v.empfaengerTyp !== filterTyp) return false;
      if (search) {
        const s = search.toLowerCase();
        return v.name.toLowerCase().includes(s) || v.beschreibung.toLowerCase().includes(s);
      }
      return true;
    });
  }, [filterTyp, search]);

  const handleSelectVorlage = useCallback((v: SchreibenVorlage) => {
    setSelectedVorlage(v);
    setBetreff(v.betreff);
    setInhalt(v.inhalt);
  }, []);

  const handleUseVorlage = useCallback((v: SchreibenVorlage) => {
    handleSelectVorlage(v);
    setActiveView('neu');
  }, [handleSelectVorlage]);

  const handleSelectKontakt = useCallback((k: CRMKontakt) => {
    setEmpName(k.name || '');
    setEmpFirma(k.firma || '');
    setEmpStrasse(k.adresse || '');
    setEmpPlzOrt([k.plz, k.ort].filter(Boolean).join(' '));
    setEmpEmail(k.email || '');
    setShowKontaktPicker(false);
    toast.success(`Kontakt "${k.name}" übernommen`);
  }, []);

  const handleDownloadPDF = useCallback(() => {
    if (!betreff || !inhalt) {
      toast.error('Bitte Betreff und Inhalt ausfüllen');
      return;
    }
    const doc = generatePDF(
      { name: empName || 'Empfänger', firma: empFirma, strasse: empStrasse, plzOrt: empPlzOrt },
      betreff,
      inhalt,
      aktenzeichen || undefined,
    );
    const filename = `Schreiben_${aktenzeichen || 'Koop'}_${localIsoDate(new Date())}.pdf`;
    doc.save(filename);
    toast.success('PDF heruntergeladen');
  }, [empName, empFirma, empStrasse, empPlzOrt, betreff, inhalt, aktenzeichen]);

  const handlePrint = useCallback(() => {
    if (!betreff || !inhalt) {
      toast.error('Bitte Betreff und Inhalt ausfüllen');
      return;
    }
    const doc = generatePDF(
      { name: empName || 'Empfänger', firma: empFirma, strasse: empStrasse, plzOrt: empPlzOrt },
      betreff,
      inhalt,
      aktenzeichen || undefined,
    );
    const blobUrl = doc.output('bloburl');
    window.open(blobUrl as unknown as string, '_blank');
  }, [empName, empFirma, empStrasse, empPlzOrt, betreff, inhalt, aktenzeichen]);

  const handleSendEmail = useCallback(async () => {
    if (!empEmail) {
      toast.error('Bitte E-Mail-Adresse des Empfängers angeben');
      return;
    }
    if (!betreff || !inhalt) {
      toast.error('Bitte Betreff und Inhalt ausfüllen');
      return;
    }
    setSending(true);
    try {
      // Build HTML email body
      const htmlBody = `
        <div style="font-family: Georgia, serif; max-width: 600px; margin: 0 auto;">
          <div style="border-bottom: 2px solid #C41E3A; padding: 20px 0;">
            <strong style="font-size: 16px;">${BRIEFKOPF.firma}</strong><br/>
            <span style="color: #666; font-size: 12px;">${BRIEFKOPF.inhaber} — ${BRIEFKOPF.qualifikation}</span>
          </div>
          <div style="padding: 20px 0;">
            <p style="font-weight: bold; margin-bottom: 16px;">${betreff}</p>
            <div style="white-space: pre-line; line-height: 1.6; color: #333;">${inhalt}</div>
          </div>
          <div style="border-top: 1px solid #ddd; padding-top: 10px; font-size: 10px; color: #999;">
            ${BRIEFKOPF.firma} · ${BRIEFKOPF.strasse} · ${BRIEFKOPF.plz} ${BRIEFKOPF.ort}<br/>
            Tel: ${BRIEFKOPF.telefon} · ${BRIEFKOPF.email}<br/>
            IBAN: ${BRIEFKOPF.iban} · ${BRIEFKOPF.bank}
          </div>
        </div>
      `;
      await sendEmail(empEmail, betreff, htmlBody);
      toast.success(`E-Mail an ${empEmail} gesendet`);
    } catch (err: any) {
      toast.error(`Fehler beim Senden: ${err?.message || 'Unbekannt'}`);
    } finally {
      setSending(false);
    }
  }, [empEmail, betreff, inhalt, sendEmail]);

  const empfaengerPreview = useMemo(() => ({
    name: empName,
    firma: empFirma,
    strasse: empStrasse,
    plzOrt: empPlzOrt,
  }), [empName, empFirma, empStrasse, empPlzOrt]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Informationsschreiben</h1>
          <p className="text-sm text-gray-500 mt-1">Professionelle Schreiben mit Briefkopf erstellen, als PDF speichern und per E-Mail versenden</p>
        </div>
        <button
          onClick={() => setActiveView('neu')}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Neues Schreiben
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-gray-200">
        {[
          { id: 'vorlagen' as const, label: 'Vorlagen', icon: FileText },
          { id: 'neu' as const, label: 'Neues Schreiben', icon: Edit },
        ].map(tab => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeView === tab.id
                  ? 'border-[#C41E3A] text-[#C41E3A]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <TabIcon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* VORLAGEN VIEW */}
      {activeView === 'vorlagen' && (
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Vorlage suchen..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setFilterTyp('alle')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  filterTyp === 'alle' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Alle
              </button>
              {(Object.entries(EMPFAENGER_CONFIG) as [EmpfaengerTyp, typeof EMPFAENGER_CONFIG['auftraggeber']][]).map(([key, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setFilterTyp(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      filterTyp === key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {cfg.label}
                  </button>
                );
              })}
            </div>

            <div className="space-y-2">
              {filteredVorlagen.map(vorlage => {
                const empCfg = EMPFAENGER_CONFIG[vorlage.empfaengerTyp];
                const EmpIcon = empCfg.icon;
                return (
                  <button
                    key={vorlage.id}
                    onClick={() => handleSelectVorlage(vorlage)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                      selectedVorlage?.id === vorlage.id
                        ? 'border-[#C41E3A] bg-red-50/50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${empCfg.color}15` }}>
                      <EmpIcon className="w-5 h-5" style={{ color: empCfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-semibold text-gray-900">{vorlage.name}</span>
                      </div>
                      <p className="text-xs text-gray-500">{vorlage.beschreibung}</p>
                    </div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: `${empCfg.color}15`, color: empCfg.color }}>
                      {empCfg.label}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Vorschau */}
          <div className="w-full lg:w-[480px] flex-shrink-0">
            {selectedVorlage ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Vorschau: {selectedVorlage.name}</h3>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => handleUseVorlage(selectedVorlage)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#C41E3A] text-white rounded-lg text-xs font-medium hover:bg-[#A3172F] transition-colors"
                    >
                      <Edit className="w-3.5 h-3.5" />
                      Verwenden
                    </button>
                  </div>
                </div>

                <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs font-semibold text-blue-700 mb-1.5">Variablen in dieser Vorlage:</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedVorlage.variablen.map(v => (
                      <span key={v} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-mono">{`{{${v}}}`}</span>
                    ))}
                  </div>
                </div>

                <BriefkopfPreview betreff={selectedVorlage.betreff} inhalt={selectedVorlage.inhalt} />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[500px] text-gray-300">
                <FileSignature className="w-12 h-12 mb-3" />
                <p className="text-sm">Vorlage auswählen für Vorschau</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* NEUES SCHREIBEN VIEW */}
      {activeView === 'neu' && (
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Neues Schreiben erstellen</h3>

              {/* Vorlage wählen */}
              <div className="mb-4">
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Vorlage</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]"
                  onChange={e => {
                    const v = VORLAGEN.find(v => v.id === e.target.value);
                    if (v) handleSelectVorlage(v);
                  }}
                  value={selectedVorlage?.id || ''}
                >
                  <option value="">Vorlage auswählen...</option>
                  {VORLAGEN.map(v => (
                    <option key={v.id} value={v.id}>{v.name} ({EMPFAENGER_CONFIG[v.empfaengerTyp].label})</option>
                  ))}
                </select>
              </div>

              {/* Empfänger aus CRM */}
              <div className="mb-4 flex items-center gap-3">
                <button
                  onClick={() => setShowKontaktPicker(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors"
                >
                  <Users className="w-3.5 h-3.5" />
                  Kontakt aus CRM wählen
                </button>
                {empEmail && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {empEmail}
                  </span>
                )}
              </div>

              {/* Empfänger */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Empfänger Name *</label>
                  <input type="text" placeholder="Name / Firma" value={empName} onChange={e => setEmpName(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Firma</label>
                  <input type="text" placeholder="Firma (optional)" value={empFirma} onChange={e => setEmpFirma(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div className="col-span-1">
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">E-Mail</label>
                  <input type="email" placeholder="E-Mail-Adresse" value={empEmail} onChange={e => setEmpEmail(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Straße</label>
                  <input type="text" placeholder="Straße + Hausnr." value={empStrasse} onChange={e => setEmpStrasse(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">PLZ / Ort</label>
                  <input type="text" placeholder="PLZ Ort" value={empPlzOrt} onChange={e => setEmpPlzOrt(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
                </div>
              </div>

              {/* Betreff */}
              <div className="mb-4">
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Betreff</label>
                <input
                  type="text"
                  placeholder="Betreff des Schreibens"
                  value={betreff}
                  onChange={e => setBetreff(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]"
                />
              </div>

              {/* Inhalt */}
              <div className="mb-4">
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Inhalt</label>
                <textarea
                  rows={12}
                  placeholder="Schreiben Sie hier den Inhalt..."
                  value={inhalt}
                  onChange={e => setInhalt(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A] resize-none leading-relaxed"
                />
              </div>

              {/* Aktenzeichen */}
              <div className="mb-6">
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Aktenzeichen (optional)</label>
                <input type="text" placeholder="z.B. AK2026001" value={aktenzeichen} onChange={e => setAktenzeichen(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  onClick={handleSendEmail}
                  disabled={sending}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors disabled:opacity-50"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Per E-Mail senden
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Als PDF speichern
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Drucken
                </button>
              </div>
            </div>
          </div>

          {/* Live-Vorschau */}
          <div className="w-full lg:w-[480px] flex-shrink-0">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Live-Vorschau</h3>
            <BriefkopfPreview
              empfaenger={empfaengerPreview}
              betreff={betreff}
              inhalt={inhalt}
              aktenzeichen={aktenzeichen}
            />
          </div>
        </div>
      )}

      {/* Kontakt-Picker Modal */}
      {showKontaktPicker && (
        <KontaktPicker
          onSelect={handleSelectKontakt}
          onClose={() => setShowKontaktPicker(false)}
        />
      )}
    </div>
  );
}
